import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, ShieldCheck, Info } from "lucide-react";

const BUNDLE_PLAN_CODE = "PLN_denfrhur2hx1oo5";
const LAUNCH_PRICE = 199;
const STANDARD_PRICE = 199;
const SAVINGS = 347;

const BUNDLE_FEATURES = [
  "Parent — browse events, vendors and meet-ups",
  "Vendor — be listed in the local directory",
  "Event Creator — publish community events",
  "Marketplace User — sell physical products",
];

export default function BundleSubscribe() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state || {};
  const [email, setEmail] = useState(prefill.email || "");
  const [agreeLaunchTerms, setAgreeLaunchTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [scriptReady, setScriptReady] = useState(!!window.PaystackPop);

  useEffect(() => {
    api.get("/billing/public-config")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ public_key: "", gateway_ready: false }));
  }, []);

  useEffect(() => {
    if (window.PaystackPop) { setScriptReady(true); return; }
    const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
    if (existing) {
      existing.addEventListener("load", () => setScriptReady(true));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://js.paystack.co/v1/inline.js";
    s.async = true;
    s.onload = () => setScriptReady(true);
    document.body.appendChild(s);
  }, []);

  const canSubscribe = agreeLaunchTerms && !!email && scriptReady && config?.gateway_ready && !busy;

  const subscribe = () => {
    if (!canSubscribe) return;
    if (!window.PaystackPop || !config?.public_key) {
      toast.error("Payment gateway not ready — please try again in a moment.");
      return;
    }
    setBusy(true);
    try {
      const handler = window.PaystackPop.setup({
        key: config.public_key,
        email,
        plan: BUNDLE_PLAN_CODE,
        currency: config.currency || "ZAR",
        metadata: {
          plan_id: "user_bundle_launch",
          bundle: true,
          custom_fields: [
            { display_name: "Membership", variable_name: "membership", value: "User Bundle Deal — All 4 Roles (Launch)" },
          ],
        },
        callback: () => {
          setBusy(false);
          toast.success("Payment successful — let's set up your 4 roles!");
          navigate("/bundle-onboarding");
        },
        onClose: () => {
          setBusy(false);
          toast.message("Checkout closed — you can resume anytime.");
        },
      });
      handler.openIframe();
    } catch (e) {
      setBusy(false);
      toast.error("Could not start checkout");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 grain" data-testid="bundle-subscribe-page">
      <div className="text-center mb-8 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Compass Kids membership</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Your User Bundle Deal</h1>
        <p className="text-forest/60 mt-2 max-w-xl mx-auto">
          All 4 roles — Parent, Vendor, Event Creator and Marketplace User — unlocked under a single subscription.
        </p>
      </div>

      {config && !config.gateway_ready && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-sm text-amber-800 text-center" data-testid="gateway-setup-banner">
          Payment gateway is in setup mode. Live billing activates once the Paystack keys are added.
        </div>
      )}

      {!prefill.email && (
        <div className="max-w-md mx-auto mb-6 bg-white/80 border border-sage/20 rounded-2xl p-4 shadow-sm">
          <label className="block text-xs font-bold text-forest/70 uppercase tracking-wide mb-1" htmlFor="bundle-email">Email for your bundle receipt</label>
          <input
            id="bundle-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-xl bg-softstone/60 border border-sage/30 px-4 py-2 text-sm"
            data-testid="bundle-email-input"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto mb-8" data-testid="bundle-plan-grid">
        <div
          className="text-left rounded-3xl border-2 border-sage ring-2 ring-sage/40 bg-white p-6 transition-all shadow-sm"
          data-testid="bundle-plan-card-launch"
        >
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full mb-3">
            <Sparkles className="w-3 h-3" /> Launch special
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black font-heading text-forest">R{LAUNCH_PRICE}</span>
            <span className="text-sm text-forest/50">/month</span>
          </div>
          <p className="text-sm font-bold text-forest mt-1">
            User Bundle — All 4 Roles <span className="text-xs font-semibold text-terracotta-dark">Limited Time</span>
          </p>
          <ul className="mt-4 space-y-2">
            {BUNDLE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-forest/70">
                <CheckCircle2 className="w-3.5 h-3.5 text-sage-dark shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/40 p-3 text-left" data-testid="launch-terms-block">
            <p className="text-[11px] text-forest/80 leading-relaxed mb-2" data-testid="launch-terms-text">
              This R{LAUNCH_PRICE}/month launch offer is only available for a strictly limited time. Sign up now to lock in this discounted rate for your first 12 months. Thereafter, your subscription will automatically resume at the standard price of R{STANDARD_PRICE}/month. You save R{SAVINGS} compared to the combined individual plan price. By selecting this option, you agree to these terms and conditions.
            </p>
            <label className="flex items-start gap-2 text-xs font-bold text-forest cursor-pointer select-none" data-testid="launch-terms-label">
              <input
                type="checkbox"
                checked={agreeLaunchTerms}
                onChange={(e) => setAgreeLaunchTerms(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                data-testid="launch-terms-checkbox"
              />
              <span>I have read and agree to the terms above.</span>
            </label>
          </div>
        </div>

        <div
          className="text-left rounded-3xl border-2 border-sage/20 bg-softstone/50 opacity-60 cursor-not-allowed p-6 transition-all shadow-sm"
          aria-disabled="true"
          data-testid="bundle-plan-card-standard"
        >
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-forest/10 text-forest/60 px-2.5 py-1 rounded-full mb-3" data-testid="bundle-plan-unavailable">
            Currently unavailable
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black font-heading text-forest">R{STANDARD_PRICE}</span>
            <span className="text-sm text-forest/50">/month</span>
          </div>
          <p className="text-sm font-bold text-forest mt-1">Standard Rate</p>
          <ul className="mt-4 space-y-2">
            {BUNDLE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-forest/60">
                <CheckCircle2 className="w-3.5 h-3.5 text-sage-dark/50 shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-4 text-center text-xs font-bold py-1.5 rounded-full bg-forest/10 text-forest/50">
            Unavailable
          </div>
        </div>
      </div>

      <div className="text-center">
        <p
          className="text-[11px] text-forest/60 mb-3 flex items-start sm:items-center justify-center gap-1.5 max-w-md mx-auto leading-snug"
          data-testid="r1-verification-notice"
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
          <span>Paystack charges a refundable R1 card verification fee to activate your payment method. It's refunded in full and is not part of your subscription cost.</span>
        </p>
        <Button
          size="lg"
          onClick={subscribe}
          disabled={!canSubscribe}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg px-10 hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="bundle-subscribe-now-btn"
        >
          {busy ? "Redirecting to Paystack..." : "Subscribe Now"}
        </Button>
        <p className="text-xs text-forest/50 mt-3 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure payment via Paystack · Billed monthly · Cancel anytime
        </p>
        <p className="text-xs text-forest/50 mt-4">
          Changed your mind? <Link to="/register" className="text-terracotta-dark font-bold hover:underline" data-testid="bundle-back-to-register">Back to sign up</Link>
        </p>
      </div>
    </div>
  );
}
